import { nextTick } from 'vue';
import {CommonRegistry} from './common/inbound.ts'
import { useServiceStore } from '@/stores/useServiceStore';
import { getIframeBaseUrl } from "@/utils/platform";
import { isAuthorizedAction } from "@/utils/security/messaging";

const inboundModules = import.meta.glob('./iframes/*/inbound.ts');
/**
 * TO ADD A NEW IFRAME SERVICE:
 * 1. Navigate to: /services/iframeBridge/iframes
 * 2. Clone the 'deliveryApp' folder and rename it to your [serviceId].
 * Note: The folder name must strictly match the service ID (in suiteServiceParams.serviceId).
 * 3. Update 'actions.ts' to define your Inbound (incoming) and Outbound (outgoing) message types.
 * 4. Ensure 'inbound.ts' and 'outbound.ts' import their own Action types from your local 'actions.ts'.
 *      should look like this: import { OutboundAction } from './actions.ts'
 * * ARCHITECTURE ROLES:
 * - inbound.ts: Handles business logic, plugin execution, and service orchestration.
 * - outbound.ts: Pure transport layer; handles message delivery without business logic.
 * * USAGE EXAMPLE:
 * import SendIframe from "@/services/IframeBridge/iframes/deliveryApp/outbound.ts";
 * SendIframe.onDeviceResume({});
 */
export class IframeBridge {
    static async send<T, K extends keyof T>(
        action: K,
        data: T[K],
        options?: { sendToAllIframes?: boolean }
    ) {
        const forceAll = options?.sendToAllIframes ?? false;
        const serviceStore = useServiceStore();
        const activeId = serviceStore.selected?.id;

        if (!forceAll && !activeId) {
            console.warn(`IframeBridge: Cannot send targeted action [${String(action)}] because no active service ID is selected.`);
            return;
        }

        await nextTick();

        const selector = forceAll
            ? '[data-service-id]'
            : `[data-service-id="${activeId}"]`;

        const containers = document.querySelectorAll(selector);
        const targetIframes: HTMLIFrameElement[] = [];

        for (const container of containers) {
            const iframe = container.querySelector('iframe');
            if (!iframe) continue;

            if (forceAll) {
                targetIframes.push(iframe);
            } else {
                const isHiddenByIonic = container.classList.contains('ion-page-hidden') || container.getAttribute('aria-hidden') === 'true';
                const isDisplayNone = (container as HTMLElement).offsetParent === null;

                if (!isHiddenByIonic && !isDisplayNone) {
                    targetIframes.push(iframe);
                }
            }
        }

        if (targetIframes.length > 0) {
            const targetOrigin = await getIframeBaseUrl();
            let originString = '*';

            try {
                const url = new URL(targetOrigin);
                originString = url.origin;
            } catch (e) {
                console.error("IframeBridge: Invalid target origin URL, falling back to '*'", targetOrigin, e);
            }

            targetIframes.forEach((iframe) => {
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage({
                        type: 'HEILA_ACTION',
                        action,
                        data
                    }, originString);
                }
            });
        } else {
            console.warn(`IframeBridge: No target iframes matched criteria for action: ${String(action)}`);
        }
    }

    static async handle(
        frameId: string,
        action: string,
        data: any
    ) {
        if (!isAuthorizedAction(action)) {
            console.warn(`[Security] Action ${action} rejected by authorization policy.`);
            return;
        }

        // prioritize common actions first
        if (CommonRegistry && action in CommonRegistry) {
            try {
                (CommonRegistry as any)[action](data);
            } catch (err) {
                console.error(`IframeBridge: Failed executing global action [${action}]`, err);
            }
            return;
        }

        const fileTargetKey = `./iframes/${frameId}/inbound.ts`;
        const foundInboundAction = inboundModules[fileTargetKey];

        if (foundInboundAction) {
            try {
                const iframe = await foundInboundAction() as any;
                if (iframe?.ModuleRegistry && action in iframe.ModuleRegistry) {
                    iframe.ModuleRegistry[action](data);
                } else {
                    console.error("Not supported action for local post message", action, data);
                }
            } catch (e) {
                console.error("Execution error inside local post message handler", action, e);
            }
        } else {
            console.warn(`Current service "${frameId}" does not have supported module or local file path.`);
        }
    }
}